/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  ShieldCheck,
  AlertCircle,
  ArrowLeft,
  Shield,
} from "lucide-react";
import { AdminContext } from "../../../context/AdminContext";
import {
  ADMIN_ACCESS,
  ADMIN_DEPARTMENTS,
  ADMIN_MODULE_LABELS,
  ALL_ADMIN_ACCESS,
  getDepartmentAccess,
  normalizeAdminAccess,
} from "../../../utils/adminAccess";

const ASSIGNABLE_ADMIN_ACCESS = ALL_ADMIN_ACCESS.filter(
  (access) => access !== ADMIN_ACCESS.SERVICE_CATEGORIES,
);

const AddSubAdmin = () => {
  const { createSubAdmin } = useContext(AdminContext);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    adminDepartment: "finance",
    adminAccess: getDepartmentAccess("finance"),
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleDepartmentChange = (e) => {
    const adminDepartment = e.target.value;
    setFormData((prev) => ({
      ...prev,
      adminDepartment,
      adminAccess:
        adminDepartment === "custom"
          ? prev.adminAccess
          : getDepartmentAccess(adminDepartment),
    }));
  };

  const toggleAccess = (access) => {
    setFormData((prev) => {
      const hasAccess = prev.adminAccess.includes(access);
      return {
        ...prev,
        adminDepartment: "custom",
        adminAccess: normalizeAdminAccess(
          hasAccess
            ? prev.adminAccess.filter((item) => item !== access)
            : [...prev.adminAccess, access],
        ),
      };
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Full name is required";

    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    if (formData.adminAccess.length === 0) {
      newErrors.adminAccess = "Choose at least one dashboard area";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      setShowConfirmModal(true);
    }
  };

  const handleConfirm = () => {
    createSubAdmin(
      formData.name,
      formData.email,
      formData.password,
      formData.adminAccess,
      formData.adminDepartment,
    );
    setShowConfirmModal(false);
    setSuccess(true);
    // Reset or redirect
    setTimeout(() => {
      navigate("/admins/list");
    }, 2000);
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      <div className="grid lg:grid-cols-12 gap-8">
        {/* Left Side - Info Panel (Hidden on mobile) */}
        <div className="lg:col-span-5 hidden lg:block">
          <div className="bg-card dark:bg-dark-surface rounded-[2rem] p-8 h-full border border-gray-100 dark:border-dark-border shadow-sm">
            <div className="flex flex-col h-full">
              <div className="mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 text-primary mb-6">
                  <ShieldCheck size={40} />
                </div>
                <h2 className="text-2xl font-bold text-text dark:text-dark-text mb-3">
                  Create Sub-Admin Account
                </h2>
                <p className="text-gray-600 dark:text-dark-muted">
                  Add a new administrator with limited permissions to help
                  manage the platform.
                </p>
              </div>

              <div className="mt-auto space-y-6">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50/50 dark:bg-gray-800/30 border border-gray-100 dark:border-dark-border">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                    <Shield size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-text dark:text-dark-text">
                      Limited Access
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-dark-muted">
                      Sub-admins have restricted permissions
                    </p>
                  </div>
                </div>

                <div className="text-sm text-gray-500 dark:text-dark-muted space-y-2">
                  <p className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-emerald-500" />
                    <span>Can view and manage user data</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-emerald-500" />
                    <span>Cannot modify system settings</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-emerald-500" />
                    <span>Access to specific modules only</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="lg:col-span-7">
          {/* Mobile Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="mb-6 lg:hidden flex items-center gap-2 text-gray-600 dark:text-dark-muted hover:text-text dark:hover:text-dark-text"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back</span>
          </button>

          {/* Mobile Header */}
          <div className="text-center mb-8 lg:hidden">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 text-primary mb-4">
              <ShieldCheck size={32} />
            </div>
            <h1 className="text-2xl font-bold text-text dark:text-dark-text">
              Add Sub-Admin
            </h1>
            <p className="text-gray-500 dark:text-dark-muted mt-2">
              Create a new administrative account
            </p>
          </div>

          {/* Form Container */}
          <div className="bg-card dark:bg-dark-surface rounded-3xl lg:rounded-[2rem] shadow-sm border border-gray-100 dark:border-dark-border p-6 sm:p-8 lg:p-10">
            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 flex items-center gap-3 text-emerald-700 dark:text-emerald-300 animate-in fade-in zoom-in duration-300">
                <CheckCircle size={20} />
                <span className="font-medium">
                  Sub-Admin account created successfully! Redirecting...
                </span>
              </div>
            )}

            {/* Desktop Form Header */}
            <div className="hidden lg:block mb-8">
              <h2 className="text-2xl font-bold text-text dark:text-dark-text">
                Account Details
              </h2>
              <p className="text-gray-500 dark:text-dark-muted mt-1">
                Enter the new administrator&apos;s information
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 lg:space-y-8">
              {/* Name Input */}
              <div>
                <label className="block text-sm font-semibold text-text dark:text-dark-text mb-3">
                  Full Name
                </label>
                <div className="relative group">
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/5 to-transparent group-focus-within:from-primary/10 transition-all duration-300" />
                  <User
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary transition-colors z-10"
                    size={20}
                  />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`relative w-full pl-12 pr-4 py-4 rounded-xl border bg-white dark:bg-gray-900 text-text dark:text-dark-text outline-none focus:ring-2 transition-all text-base ${
                      errors.name
                        ? "border-red-500 focus:ring-red-200"
                        : "border-gray-200 dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                    placeholder="Enter full name"
                  />
                </div>
                {errors.name && (
                  <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                    <AlertCircle size={14} /> {errors.name}
                  </p>
                )}
              </div>

              {/* Email Input */}
              <div>
                <label className="block text-sm font-semibold text-text dark:text-dark-text mb-3">
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/5 to-transparent group-focus-within:from-primary/10 transition-all duration-300" />
                  <Mail
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary transition-colors z-10"
                    size={20}
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`relative w-full pl-12 pr-4 py-4 rounded-xl border bg-white dark:bg-gray-900 text-text dark:text-dark-text outline-none focus:ring-2 transition-all text-base ${
                      errors.email
                        ? "border-red-500 focus:ring-red-200"
                        : "border-gray-200 dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                    placeholder="admin@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                    <AlertCircle size={14} /> {errors.email}
                  </p>
                )}
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-sm font-semibold text-text dark:text-dark-text mb-3">
                  Password
                </label>
                <div className="relative group">
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/5 to-transparent group-focus-within:from-primary/10 transition-all duration-300" />
                  <Lock
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary transition-colors z-10"
                    size={20}
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`relative w-full pl-12 pr-12 py-4 rounded-xl border bg-white dark:bg-gray-900 text-text dark:text-dark-text outline-none focus:ring-2 transition-all text-base ${
                      errors.password
                        ? "border-red-500 focus:ring-red-200"
                        : "border-gray-200 dark:border-dark-border focus:ring-primary/20 focus:border-primary"
                    }`}
                    placeholder="Minimum 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary dark:hover:text-primary transition-colors z-10"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                    <AlertCircle size={14} /> {errors.password}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-text dark:text-dark-text mb-3">
                  Department Preset
                </label>
                <select
                  name="adminDepartment"
                  value={formData.adminDepartment}
                  onChange={handleDepartmentChange}
                  className="w-full px-4 py-4 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-gray-900 text-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  {Object.entries(ADMIN_DEPARTMENTS).map(([key, department]) => (
                    <option key={key} value={key}>
                      {department.label}
                    </option>
                  ))}
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-text dark:text-dark-text mb-3">
                  Dashboard Access
                </label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {ASSIGNABLE_ADMIN_ACCESS.map((access) => (
                    <label
                      key={access}
                      className={`flex items-center gap-3 rounded-xl border p-3 text-sm font-medium transition-colors ${
                        formData.adminAccess.includes(access)
                          ? "border-primary bg-primary/5 text-primary"
                          : "border-gray-200 dark:border-dark-border text-gray-600 dark:text-dark-muted"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.adminAccess.includes(access)}
                        onChange={() => toggleAccess(access)}
                        className="h-4 w-4 accent-primary"
                      />
                      <span>{ADMIN_MODULE_LABELS[access]}</span>
                    </label>
                  ))}
                </div>
                {errors.adminAccess && (
                  <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                    <AlertCircle size={14} /> {errors.adminAccess}
                  </p>
                )}
              </div>

              {/* Form Actions */}
              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full py-4 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white rounded-xl font-bold shadow-lg shadow-primary/20 dark:shadow-primary/10 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 text-base"
                >
                  Create Account
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/admins/list")}
                  className="w-full mt-4 py-3.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-text dark:text-dark-text rounded-xl font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 flex items-center justify-center mx-auto mb-6 text-primary">
              <ShieldCheck size={36} />
            </div>
            <h3 className="text-xl font-bold text-text dark:text-dark-text mb-3">
              Confirm Account Creation
            </h3>
            <p className="text-gray-500 dark:text-dark-muted mb-8">
              You are about to create a new Sub-Admin account for{" "}
              <span className="font-bold text-text dark:text-dark-text">
                {formData.name}
              </span>
              . This account will have limited administrative permissions.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-6 py-3.5 rounded-xl bg-gray-100 dark:bg-gray-800 text-text dark:text-dark-text font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 px-6 py-3.5 rounded-xl bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white font-bold transition-colors shadow-lg shadow-primary/20 dark:shadow-primary/10"
              >
                Create Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddSubAdmin;
